import type { Call, Device } from '@twilio/voice-sdk';
import type { TwilioError } from '@twilio/voice-sdk/es5/twilio/errors';

import { cleanChatSystemTags } from '@/common/chatSystemTags';
import { filterObject } from '@/common/utilities';
import { parseErrorMessage } from '@/common/error';
import { analytics, VOICE_EVENTS } from '@/common/logs';

import { EventEmitter } from '../common/EventEmitter';
import type { VisitorDetail } from '../common/PleasedSdk.types';
import { PleasedApi } from '../PleasedApi';
import { $pr } from '../PleasedReporting';
import { isVoiceSupported } from '../PleasedClient/client.pleased';

import { Bridge } from '@/common/bridge';
import * as env from '@/common/environment';
import { useI18n } from '@/plugins/i18n';
import { devLog } from '@/common/debug';

import type {
  AttachNativeVoiceSessionParams,
  NativeHostSessionParams,
  NativeVoiceCallState,
  NativeVoiceBridge,
  PleasedVoiceChatEvents,
  VoiceChatOptions,
} from './PleasedVoiceChat.types';
import { logEvent, logger } from '@/common/api/logger';
import { throttle } from '@/vendor/pleased-sdk/util';

const VOICE_SAMPLE_TRACK_INTERVAL = 10_000;
const VOICE_SAMPLE_MAX_SAMPLES = 10;
const SILENT_CALL_THRESHOLD = 10;

/** Parent bridge: `START_CALL` postMessage / native bus (Sporty host contract). */
export const createParentNativeVoiceBridge = (): NativeVoiceBridge => ({
  sendStartCall: (payload) => {
    analytics.trackVoiceEvent({
      name: VOICE_EVENTS.VOICE_NATIVE_START_CALL_SEND,
      metadata: { payload },
    });

    Bridge.sendToParent('START_CALL', payload);
  },
  subscribeCallState: (handler) => {
    Bridge.listenFromParent<{ callState?: NativeVoiceCallState }>('START_CALL', (data) => {
      const callState = data.callState;
      if (!callState) {
        analytics.trackVoiceEvent({
          name: VOICE_EVENTS.VOICE_NATIVE_CALL_STATE_MISSING,
          metadata: { payload: data },
        });
        logger.error('[CHATROOM] Call state is not defined');
        return;
      }
      handler(callState);
    });
  },
});

/**
 * Subscribe to native call-state updates, then send `START_CALL` through the given `bridge`.
 * For the default parent WebView bridge, use {@link PleasedVoiceChat.startNativeHostWithParent}.
 */
export const attachNativeVoiceSession = (params: AttachNativeVoiceSessionParams): void => {
  const { twilioJWT, voiceChatId, requesterId, notification, bridge, onConnected, onDisconnected } = params;

  analytics.trackVoiceEvent({
    name: VOICE_EVENTS.VOICE_NATIVE_START_CALL,
    metadata: {
      twilioJWT: twilioJWT.slice(0, 10),
      requesterId,
      voiceChatId,
    },
  });

  bridge.subscribeCallState((callState: NativeVoiceCallState) => {
    analytics.trackVoiceEvent({
      name: VOICE_EVENTS.VOICE_NATIVE_CALL_STATE,
      metadata: { callState },
    });

    if (!callState) {
      logger.error('[CHATROOM] Call state is not defined', {
        voiceChatId: voiceChatId || 'unknown',
        requesterId: requesterId || 'unknown',
      });
      return;
    }

    if (callState === 'Connected') {
      onConnected?.();
      return;
    }

    if (callState === 'Disconnected') {
      onDisconnected?.();
    }
  });

  bridge.sendStartCall({
    useTwilioVoice: true,
    accessToken: twilioJWT,
    callParams: { voiceChatId, requesterId },
    notification,
  });
};

export class PleasedVoiceChat extends EventEmitter<PleasedVoiceChatEvents> {
  /**
   * Hand audio to the host using the parent `START_CALL` bridge (no custom `bridge`; use {@link attachNativeVoiceSession} for that).
   */
  static startNativeHostWithParent(params: NativeHostSessionParams): void {
    attachNativeVoiceSession({
      ...params,
      bridge: createParentNativeVoiceBridge(),
    });
  }

  protected voiceChatId!: number;

  protected visitor: null | VisitorDetail = null;
  protected twilioDevice: null | Device = null;
  protected call: Call | null = null;
  protected lifecycle: 'not-created' | 'creating' | 'created' | 'starting' | 'ready' | 'connected' | 'disconnected' =
    'not-created';
  protected twilioJWT: null | string = null;
  protected timestampWhenConnected: number | null = null;

  protected silentCallCount = 0;
  protected silentCallEventSent = false;
  protected hasNonEmptySampleBeforeThreshold = false;

  constructor(protected options: VoiceChatOptions) {
    super();
  }

  get id() {
    return this.voiceChatId || 'new';
  }

  get isActive() {
    return this.lifecycle === 'ready' || this.lifecycle === 'connected';
  }

  get status() {
    return this.lifecycle;
  }

  get connectedTimestamp() {
    return this.timestampWhenConnected;
  }

  protected getParentStartCallNotification(): { title: string; text: string } {
    const i18n = useI18n();
    return {
      title: i18n.$t('page.voice.app.notification.title') as string,
      text: i18n.$t('page.voice.app.notification.text', { appName: env.get('appName') }) as string,
    };
  }

  async create() {
    if (this.voiceChatId) return this;
    const {
      description,
      extraFields,
      ticketId,
      user,
      chatbotSessionId,
      aiSessionId,
      aiVoiceChatSessionId,
      userSelections = [],
      chatTags,
      autoAddTags = [],
      customerAppVersion,
      isGuest: isGuestOption,
    } = this.options;

    $pr.voiceChatInitiated();

    const tags = [...autoAddTags, ...(chatTags?.length ? cleanChatSystemTags(chatTags) : [])];
    await logEvent({ step: 'PleasedVoiceChat create start' });

    const voiceSupported = await isVoiceSupported();
    await logEvent({ step: 'PleasedVoiceChat voiceSupported', voiceSupported });

    this.lifecycle = 'creating';
    await this.getTwilio();
    await logEvent({ step: 'PleasedVoiceChat getTwilio finish' });

    const isGuest = typeof isGuestOption === 'boolean' ? isGuestOption : !!user.isGuest;
    const userLogs = $pr.getLogs();

    const { requesterId, twilioJWT, voiceChatId } = await PleasedApi.createVoiceChatRequest(
      filterObject({
        description: description || '',
        extraFields: extraFields || [],
        ticketId,
        tags,
        ...(userLogs.length ? { userLogs } : {}),
        userDetails: {
          email: user.email || '',
          foreignId: user.foreignId || '',
          isGuest,
          phone: user.phone || '',
          name: user.displayName || '',
          ...(aiSessionId && isGuest
            ? { userId: user.id !== undefined && user.id !== null ? String(user.id) : '' }
            : {}),
        },
        chatbotSessionId,
        aiSessionId,
        aiVoiceChatSessionId,
        userSelections,
        customerAppVersion,
      })
    ).catch(async (e) => {
      this.lifecycle = 'not-created';
      await logEvent({ step: 'PleasedVoiceChat createVoiceChatRequest error', e });
      throw e;
    });

    await logEvent({ step: 'PleasedVoiceChat createVoiceChatRequest finish', requesterId, twilioJWT, voiceChatId });

    this.lifecycle = 'created';

    this.visitor = {
      id: requesterId,
      email: user.email || '',
      foreignId: user.foreignId || '',
      isGuest: !!user.isGuest,
      phone: user.phone || '',
      displayName: user.displayName || '',
    };

    this.voiceChatId = voiceChatId;

    this.twilioJWT = twilioJWT;

    this.event.emit('voice:created');

    return this;
  }

  /** Same side effects as Twilio `accept` (native host has no `Call` instance — pass `null`). */
  protected async notifyCallAccepted(conn: Call | null) {
    devLog.debug('[PLEASED_VOICE_CHAT] Call accepted', { voiceChatId: this.voiceChatId, connection: conn });
    this.lifecycle = 'connected';
    this.timestampWhenConnected = Date.now();
    analytics.trackVoiceEvent({
      name: VOICE_EVENTS.CALL_ACCEPTED,
      selection: this.voiceChatId,
      metadata: {
        source: conn ? 'web' : 'native',
      },
    });
    await logEvent({
      step: 'PleasedVoiceChat call accepted',
    });
    this.event.emit('voice:connect', conn);
  }

  /** Same side effects as Twilio `disconnect` (skips device teardown when there is no web `Device`). */
  protected async notifyCallDisconnected(conn: Call | null) {
    devLog.debug('[PLEASED_VOICE_CHAT] Call disconnected', { voiceChatId: this.voiceChatId, connection: conn });
    analytics.trackVoiceEvent({
      name: VOICE_EVENTS.CALL_DISCONNECTED,
      selection: this.voiceChatId,
      metadata: {
        source: conn ? 'web' : 'native',
      },
    });
    await logEvent({ step: 'PleasedVoiceChat call disconnect', conn });
    this.twilioDevice && this.twilioDevice.disconnectAll();
    this.twilioDevice && this.twilioDevice.destroy();
    this.event.emit('voice:disconnect', conn);
    this.destroy();
  }

  start() {
    // Start the voice if it's not already started, if it's already started or the voice chat was never created, then
    // This is a noop action.
    if (this.options.skipWebSDKInitialization) {
      if (this.status !== 'created' || !this.twilioJWT || !this.visitor) {
        return false;
      }
      this.lifecycle = 'starting';
      PleasedVoiceChat.startNativeHostWithParent({
        twilioJWT: this.twilioJWT,
        voiceChatId: this.voiceChatId,
        requesterId: this.visitor.id,
        notification: this.getParentStartCallNotification(),
        onConnected: () => {
          analytics.trackVoiceEvent({
            name: VOICE_EVENTS.VOICE_NATIVE_CONNECTED_CALLBACK,
            selection: this.voiceChatId,
          });
          this.notifyCallAccepted(null);
        },
        onDisconnected: () => {
          analytics.trackVoiceEvent({
            name: VOICE_EVENTS.VOICE_NATIVE_DISCONNECTED_CALLBACK,
            selection: this.voiceChatId,
          });
          this.notifyCallDisconnected(null);
        },
      });
      return true;
    }

    return Boolean(
      this.status === 'created' && this.twilioJWT && !this.twilioDevice && this.setupTwilio(this.twilioJWT)
    );
  }

  protected getLogDeviceInfo() {
    return {
      availableInputDevices: Array.from(this.twilioDevice?.audio?.availableInputDevices.values() || []),
      availableOutputDevices: Array.from(this.twilioDevice?.audio?.availableOutputDevices.values() || []),
      ringtoneDevices: Array.from(this.twilioDevice?.audio?.ringtoneDevices.get().values() || []),
      speakerDevices: Array.from(this.twilioDevice?.audio?.speakerDevices.get().values() || []),
      audioConstraints: this.twilioDevice?.audio?.audioConstraints,
      inputDevice: this.twilioDevice?.audio?.inputDevice,
      inputStream: this.twilioDevice?.audio?.inputStream,
      isOutputSelectionSupported: this.twilioDevice?.audio?.isOutputSelectionSupported,
      isVolumeSupported: this.twilioDevice?.audio?.isVolumeSupported,
    };
  }

  protected async setupTwilio(token: string) {
    this.lifecycle = 'starting';
    await logEvent({ step: 'PleasedVoiceChat setupTwilio' });

    const { Call, Device } = await this.getTwilio();
    const twilioDevice = new Device(token, {
      logLevel: 'DEBUG',
      // Set Opus as our preferred codec. Opus generally performs better, requiring less bandwidth and
      // providing better audio quality in restrained network conditions. Opus will be default in 2.0.
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      enableImprovedSignalingErrorPrecision: true,
      maxCallSignalingTimeoutMs: 30000,
    });

    twilioDevice.on('registering', async () => {
      devLog.debug('[PLEASED_VOICE_CHAT] Twilio device registering', { voiceChatId: this.voiceChatId });
      analytics.trackVoiceEvent({ name: VOICE_EVENTS.DEVICE_REGISTERING, selection: this.voiceChatId });
      await logEvent({ step: 'PleasedVoiceChat twilioDevice registering' });
    });

    const handleConnected = async () => {
      devLog.debug('[PLEASED_VOICE_CHAT] Twilio device connected', {
        voiceChatId: this.voiceChatId,
        deviceData: this.getLogDeviceInfo(),
      });
      await logEvent({ step: 'PleasedVoiceChat twilioDevice connected', deviceData: this.getLogDeviceInfo() });
    };

    twilioDevice.on('registered', async (device) => {
      devLog.debug('[PLEASED_VOICE_CHAT] Twilio device registered', { voiceChatId: this.voiceChatId, device });
      analytics.trackVoiceEvent({ name: VOICE_EVENTS.DEVICE_REGISTERED, selection: this.voiceChatId });
      await logEvent({ step: 'PleasedVoiceChat twilioDevice registered' });

      try {
        this.call = await twilioDevice.connect();
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_CONNECTED_SUCCESSFUL, selection: this.voiceChatId });
        this.lifecycle = 'ready';
        this.event.emit('voice:ready', device);
        this.handleCallAcceptance();
        devLog.debug('[PLEASED_VOICE_CHAT] Connection successful', { voiceChatId: this.voiceChatId });
        device.audio.speakerDevices.set('default');
        await handleConnected();
      } catch (e) {
        devLog.error('[PLEASED_VOICE_CHAT] Connection failed', {
          voiceChatId: this.voiceChatId,
          error: parseErrorMessage(e),
        });
        logger.error('[PLEASED_VOICE_CHAT] Connection failed', {
          voiceChatId: this.voiceChatId,
          error: parseErrorMessage(e),
        });
        analytics.trackVoiceEvent({
          name: VOICE_EVENTS.CALL_CONNECTED_FAILED,
          selection: this.voiceChatId,
          metadata: { error: parseErrorMessage(e) },
        });
        await logEvent({ step: 'PleasedVoiceChat twilioDevice connection failed' });
      }
    });

    twilioDevice.on('error', async (error: TwilioError) => {
      // TODO: error handler
      devLog.error('[PLEASED_VOICE_CHAT] Twilio device error', {
        voiceChatId: this.voiceChatId,
        error: parseErrorMessage(error),
      });
      logger.error('[PLEASED_VOICE_CHAT] Twilio device error', {
        voiceChatId: this.voiceChatId,
        error: parseErrorMessage(error),
      });

      analytics.trackVoiceEvent({
        name: VOICE_EVENTS.DEVICE_ERROR,
        selection: this.voiceChatId,
        metadata: { error: parseErrorMessage(error) },
      });
      await logEvent({ step: 'PleasedVoiceChat twilioDevice error', error });
      this.event.emit('voice:error', error);
    });

    twilioDevice.on('incoming', async (conn) => {
      await logEvent({ step: 'PleasedVoiceChat twilioDevice incoming' });
      devLog.debug('[PLEASED_VOICE_CHAT] Twilio device incoming', { voiceChatId: this.voiceChatId, connection: conn });
    });

    twilioDevice.on('unregistered', async () => {
      analytics.trackVoiceEvent({ name: VOICE_EVENTS.DEVICE_UNREGISTERED, selection: this.voiceChatId });
      await logEvent({ step: 'PleasedVoiceChat twilioDevice unregistered' });
      devLog.debug('[PLEASED_VOICE_CHAT] Twilio device unregistered', { voiceChatId: this.voiceChatId });
    });

    twilioDevice.on('deviceChange', async (device) => {
      devLog.debug('[PLEASED_VOICE_CHAT] Twilio device change', { voiceChatId: this.voiceChatId, device });
      await logEvent({ step: 'PleasedVoiceChat twilioDevice deviceChange', device });
    });

    this.twilioDevice = twilioDevice;
    analytics.trackVoiceEvent({ name: VOICE_EVENTS.DEVICE_REGISTER, selection: this.voiceChatId });
    this.twilioDevice?.register();
    await logEvent({ step: 'PleasedVoiceChat twilioDevice register' });
  }

  protected resetSilentCallTracking() {
    this.silentCallCount = 0;
    this.silentCallEventSent = false;
    this.hasNonEmptySampleBeforeThreshold = false;
  }

  protected handleCallAcceptance() {
    if (!this.call) {
      return;
    }

    this.resetSilentCallTracking();

    this.call.on('accept', async (conn) => {
      await this.notifyCallAccepted(conn);
    });

    this.call.on('error', async (error) => {
      devLog.error('[PLEASED_VOICE_CHAT] Call error', {
        voiceChatId: this.voiceChatId,
        error: parseErrorMessage(error),
      });
      logger.error('[PLEASED_VOICE_CHAT] Call error', {
        voiceChatId: this.voiceChatId,
        error: parseErrorMessage(error),
      });
      analytics.trackVoiceEvent({
        name: VOICE_EVENTS.CALL_ERROR,
        selection: this.voiceChatId,
        metadata: { error: parseErrorMessage(error) },
      });
      await logEvent({ step: 'PleasedVoiceChat call error' });
      this.event.emit('voice:error', error);
    });

    this.call.on('cancel', async () => {
      devLog.debug('[PLEASED_VOICE_CHAT] Call cancelled', { voiceChatId: this.voiceChatId });
      analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_CANCEL, selection: this.voiceChatId });
      await logEvent({ step: 'PleasedVoiceChat call cancel' });
    });

    this.call.on('reconnecting', async (twilioError) => {
      devLog.debug('[PLEASED_VOICE_CHAT] Call reconnecting', {
        voiceChatId: this.voiceChatId,
        twilioError: parseErrorMessage(twilioError),
      });
      logger.debug('[PLEASED_VOICE_CHAT] Call reconnecting', {
        voiceChatId: this.voiceChatId,
        twilioError: parseErrorMessage(twilioError),
      });
      analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_RECONNECTING, selection: this.voiceChatId });
      await logEvent({ step: 'PleasedVoiceChat call reconnecting' });
    });

    this.call.on('reconnected', async () => {
      devLog.debug('[PLEASED_VOICE_CHAT] Call reconnected', { voiceChatId: this.voiceChatId });
      analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_RECONNECTED, selection: this.voiceChatId });
      await logEvent({ step: 'PleasedVoiceChat call reconnected' });
    });

    this.call.on('disconnect', async (conn) => {
      await this.notifyCallDisconnected(conn);
    });

    let voiceSampleCount = 0;
    const throttledSampleHandler = throttle(
      async (sampleData: { packetsSent?: number; bytesSent?: number } & Record<string, unknown>) => {
        if (voiceSampleCount >= VOICE_SAMPLE_MAX_SAMPLES) {
          return;
        }
        voiceSampleCount += 1;
        analytics.trackVoiceEvent({
          name: VOICE_EVENTS.CALL_SAMPLE,
          selection: this.voiceChatId,
          metadata: sampleData,
        });
        devLog.debug('[PLEASED_VOICE_CHAT] Call sample', { voiceChatId: this.voiceChatId, sampleData });
        await logEvent({
          step: 'PleasedVoiceChat call sample',
          sample: sampleData,
          deviceData: this.getLogDeviceInfo(),
        });
      },
      VOICE_SAMPLE_TRACK_INTERVAL
    );

    this.call.on('sample', throttledSampleHandler);

    this.call.on('sample', (sampleData: { packetsSent?: number; bytesSent?: number }) => {
      const isEmpty = !sampleData.packetsSent && !sampleData.bytesSent;

      if (this.silentCallEventSent || this.hasNonEmptySampleBeforeThreshold) {
        return;
      }

      if (!isEmpty) {
        this.hasNonEmptySampleBeforeThreshold = true;
        this.silentCallCount = 0;
        return;
      }

      this.silentCallCount += 1;
      if (this.silentCallCount >= SILENT_CALL_THRESHOLD && !this.silentCallEventSent) {
        this.silentCallEventSent = true;
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.SILENT_CALL, selection: this.voiceChatId });
      }
    });
  }

  async hangUp() {
    if (this.options.skipWebSDKInitialization) {
      analytics.trackVoiceEvent({
        name: VOICE_EVENTS.VOICE_NATIVE_HANG_UP,
        selection: this.voiceChatId,
      });

      Bridge.sendToParent('NATIVE_END_CALL', { voiceChatId: this.voiceChatId });
    }

    await logEvent({ step: 'PleasedVoiceChat hangUp' });
    this.twilioDevice?.disconnectAll();
    this.twilioDevice?.destroy();
    this.destroy();
  }

  protected async destroy() {
    this.lifecycle = 'disconnected';
    await logEvent({ step: 'PleasedVoiceChat destroy' });
    this.event.all.clear();
  }

  protected async getTwilio() {
    const { Call, Device } = await import('@twilio/voice-sdk');
    return { Call, Device };
  }
}
